/**
 * Activate — public page behind a one-time activation link (spec 2026-09-24 increment A).
 * Sets the password only; the user then signs in normally (password + emailed code).
 * Never stores anything in the browser: there is no session yet.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Alert, Button, TextField, Typography } from '@mui/material';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const INVALID = 'This activation link is invalid or has expired. Ask your administrator for a new one.';

export default function Activate() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(token ? null : INVALID);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  // M-9: this page's own URL carries the live `?token=`. Without a no-referrer policy, following
  // any outbound link (or a resource fetched with the browser's default Referer policy) from here
  // could leak the token via the Referer header. Set the meta tag while mounted, restoring whatever
  // was there before (there is nothing currently, but this stays correct if that ever changes).
  useEffect(() => {
    const existing = document.querySelector('meta[name="referrer"]');
    const previousContent = existing ? existing.getAttribute('content') : null;
    const meta = existing ?? document.createElement('meta');
    if (!existing) {
      meta.setAttribute('name', 'referrer');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'no-referrer');
    return () => {
      if (previousContent !== null) meta.setAttribute('content', previousContent);
      else meta.parentNode?.removeChild(meta);
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (pw.length < 12) { setError('Password must be at least 12 characters.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/auth/activate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, password: pw }) });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body.success) setDone(true); else setError(body.error ?? INVALID);
    } catch { setError('Could not reach PRISM. Check your connection and try again.'); }
    finally { setBusy(false); }
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 440, width: '100%' }}>
        <Typography variant="h5" component="h1" gutterBottom>Set your PRISM password</Typography>
        {done ? (
          <>
            <Alert role="status" severity="success" sx={{ mb: 2 }}>✓ Your password is set. Sign in with your email and new password; we will email you a sign-in code.</Alert>
            <Button variant="contained" onClick={() => navigate('/login', { replace: true })}>Go to sign in</Button>
          </>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {error && <Alert role="alert" severity="error" sx={{ mb: 2 }}>⚠ {error}</Alert>}
            {token && (
              <>
                <TextField label="New password" type="password" autoComplete="new-password" fullWidth margin="normal"
                  value={pw} onChange={e => setPw(e.target.value)} helperText="At least 12 characters." />
                <TextField label="Confirm password" type="password" autoComplete="new-password" fullWidth margin="normal"
                  value={confirm} onChange={e => setConfirm(e.target.value)} />
                <Button type="submit" variant="contained" disabled={busy} sx={{ mt: 2 }}>Set password</Button>
              </>
            )}
          </form>
        )}
      </Box>
    </Box>
  );
}
